import type { Amount } from '@getstep/proto/common'
import type {
    Application,
    CreateApplicationRequest,
} from '@getstep/proto/underwriting'
import {
    HousingStatus,
    IncomeSource,
    LendingRequirementType,
} from '@getstep/proto/underwriting'
import { DataStore } from '@getstep/sdk/dist/store/common/DataStore'
import type { UnderwritingApplicationStore } from '@getstep/sdk/dist/store/UnderwritingApplicationStore'
import { notNullish } from '@getstep/sdk/dist/util/Assert'
import { Instant, ZonedDateTime, ZoneId } from '@js-joda/core'
import { formatAmount } from '@lib/utils/number'
import { isPrd } from '@shared/util/env'
import { action, computed, makeObservable, observable } from 'mobx'

export interface ApplicationData {
    incomeSource: IncomeSource
    annualIncome: number | null
    incomeFrequency: 'year' | 'month' | '2_weeks' | 'week'
    housingStatus: HousingStatus
    rentAmount: number | null
    creditReportConsent: boolean
}

/**
 * Result of checking lending requirements
 */
export interface LendingRequirementsCheckResult {
    /**
     * Whether all requirements are met
     */
    allRequirementsMet: boolean
    /**
     * Array of human-readable messages for unmet requirements
     */
    unmetRequirements: string[]
}

export class BorrowingStore extends DataStore {
    static readonly className = 'BorrowingStore'

    applicationData: ApplicationData = {
        incomeSource: IncomeSource.INVALID_INCOME_SOURCE,
        annualIncome: null,
        incomeFrequency: 'year',
        housingStatus: HousingStatus.INVALID_HOUSING_STATUS,
        rentAmount: null,
        creditReportConsent: false,
    }

    constructor(
        readonly underwritingApplicationStore: UnderwritingApplicationStore,
    ) {
        super()
        makeObservable(this, {
            applicationData: observable,
            hasApplication: computed,
            application: computed,
            eligibleAmount: computed,
            loanAmount: computed,
            circuitBreakerError: computed,
            setApplicationData: action,
        })
    }

    get hasApplication(): boolean {
        if (this.loaded) {
            return notNullish(this.application)
        }
        return false
    }

    get application(): Application | undefined {
        return this.underwritingApplicationStore.application
    }

    get retryApplicationAfter(): ZonedDateTime | undefined {
        const { retryAfterMs } = this.application ?? {}
        return retryAfterMs == undefined
            ? undefined
            : Instant.ofEpochMilli(+retryAfterMs).atZone(ZoneId.systemDefault())
    }

    get canRetry(): boolean {
        return (
            this.retryApplicationAfter == undefined ||
            this.retryApplicationAfter.isBefore(ZonedDateTime.now())
        )
    }

    get eligibleAmount(): Amount | undefined {
        return this.application?.products?.[0]?.eligibility?.eligibleAmount
    }

    get loanAmount(): string {
        const amount = this.eligibleAmount
        return notNullish(amount) ? formatAmount(amount) : ''
    }

    get circuitBreakerError(): boolean {
        const circuitBreaker =
            this.underwritingApplicationStore.lendingRequirements?.find(
                (lr) => lr.type === LendingRequirementType.CIRCUIT_BREAKER,
            )
        return notNullish(circuitBreaker) && !circuitBreaker.confirmed
    }

    /**
     * Checks lending requirements and returns unmet requirement messages
     *
     * @returns Result containing whether all requirements are met and list of unmet requirement messages
     *
     * @example
     * ```typescript
     * const result = borrowingStore.checkLendingRequirements()
     *
     * if (!result.allRequirementsMet) {
     *   console.log('Unmet requirements:', result.unmetRequirements)
     * }
     * ```
     */
    checkLendingRequirements(): LendingRequirementsCheckResult {
        const lendingRequirements =
            this.underwritingApplicationStore.lendingRequirements

        // If no requirements provided, assume all requirements are met
        if (!lendingRequirements || lendingRequirements.length === 0) {
            return {
                allRequirementsMet: true,
                unmetRequirements: [],
            }
        }

        const unmetRequirements: string[] = []

        lendingRequirements.forEach((requirement) => {
            if (!requirement.confirmed) {
                const message = this.getRequirementMessage(requirement.type)
                if (notNullish(message)) {
                    unmetRequirements.push(message)
                }
            }
        })

        return {
            allRequirementsMet: unmetRequirements.length === 0,
            unmetRequirements,
        }
    }

    setApplicationData(applicationData: ApplicationData) {
        this.applicationData = applicationData
    }

    /**
     * Submit borrowing application
     * @param skipApplicationData - If true, skips validation of application data (used in simplified flow where questions aren't asked)
     */
    async submitBorrowingApplication(
        skipApplicationData = false,
    ): Promise<Application> {
        // In simplified flow, skip application data validation and send empty request
        if (skipApplicationData) {
            return this.execute(() => {
                return this.underwritingApplicationStore?.createApplication({
                    info: {},
                })
            })
        }

        // Old flow: validate and submit application data
        // Strict validation for income source enum
        const validIncomeSources = Object.values(IncomeSource).filter(
            (value) => value !== IncomeSource.INVALID_INCOME_SOURCE,
        )
        // @ts-expect-error - Type inference issue
        if (!validIncomeSources.includes(this.applicationData.incomeSource)) {
            throw new Error('Invalid income source provided')
        }

        if (
            !this.applicationData.annualIncome ||
            this.applicationData.annualIncome <= 0
        ) {
            throw new Error('A valid annual income is required')
        }

        // Strict validation for housing status enum
        const validHousingStatuses = Object.values(HousingStatus).filter(
            (value) => value !== HousingStatus.INVALID_HOUSING_STATUS,
        )
        if (
            // @ts-expect-error - Type compatibility issue
            !validHousingStatuses.includes(this.applicationData.housingStatus)
        ) {
            throw new Error('Invalid housing status provided')
        }

        if (
            this.applicationData.housingStatus !== HousingStatus.FAMILY &&
            (!this.applicationData.rentAmount ||
                this.applicationData.rentAmount <= 0)
        ) {
            throw new Error('A valid rent/mortgage amount is required')
        }

        // Convert income to annual based on frequency
        let annualIncome: number
        switch (this.applicationData.incomeFrequency) {
            case 'month':
                annualIncome = this.applicationData.annualIncome * 12
                break
            case '2_weeks':
                annualIncome = this.applicationData.annualIncome * 26
                break
            case 'week':
                annualIncome = this.applicationData.annualIncome * 52
                break
            case 'year':
            default:
                annualIncome = this.applicationData.annualIncome
                break
        }

        const createRequest: CreateApplicationRequest = {
            info: {
                incomeSource: this.applicationData.incomeSource,
                annualIncome: {
                    exact: {
                        currency: 'USD',
                        value: Math.round(annualIncome).toString(),
                    },
                },
                housingStatus: this.applicationData.housingStatus,
                monthlyHousingPayment:
                    this.applicationData.housingStatus !== HousingStatus.FAMILY
                        ? {
                              exact: {
                                  currency: 'USD',
                                  value: Math.round(
                                      this.applicationData.rentAmount!,
                                  ).toString(),
                              },
                          }
                        : undefined,
            },
        }

        return this.execute(() => {
            return this.underwritingApplicationStore?.createApplication(
                createRequest,
            )
        })
    }

    protected async performLoad(isRefresh: boolean): Promise<void> {
        try {
            if (isRefresh) await this.underwritingApplicationStore.refresh()
            else await this.underwritingApplicationStore.load()
        } catch (e) {
            console.info('failed to load borrowing store', e)
        }
    }

    /**
     * Maps a lending requirement type to a human-readable error message,
     * if there is no mapping then we don't strictly enforce this requirement
     */
    private getRequirementMessage(
        type: LendingRequirementType,
    ): string | undefined {
        switch (type) {
            case LendingRequirementType.RISK_SCORE:
                return 'Account requirements not met'
            case LendingRequirementType.DIRECT_DEPOSIT:
                return 'Direct deposit enrollment required'
            case LendingRequirementType.AGE:
                return 'Age verification required'
            case LendingRequirementType.REGION:
                return isPrd() ? 'Not available in your region' : undefined
            case LendingRequirementType.EMANCIPATION:
                return 'Account ownership verification required'
            default:
                return undefined
        }
    }
}
